import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const TeamExpertise = ({ userProfile, onSave, saving }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    full_name: userProfile?.full_name || '',
    title: userProfile?.title || '',
    bio: userProfile?.bio || '',
    skills: userProfile?.skills?.join(', ') || '',
    linkedin_url: userProfile?.linkedin_url || '',
    website_url: userProfile?.website_url || ''
  });
  const [errors, setErrors] = useState({});
  const [teamMembers, setTeamMembers] = useState([
    {
      id: 1,
      name: userProfile?.full_name || 'Your Name',
      title: userProfile?.title || 'Founder/CEO',
      bio: userProfile?.bio || 'Add your professional bio...',
      skills: userProfile?.skills || [],
      linkedin_url: userProfile?.linkedin_url || '',
      isOwner: true
    }
  ]);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [newMember, setNewMember] = useState({
    name: '',
    title: '',
    bio: '',
    skills: '',
    linkedin_url: ''
  });

  // Professional skills suggestions
  const skillSuggestions = [
    'Smart Cities', 'IoT', 'Data Analytics', 'Machine Learning', 'AI',
    'Urban Planning', 'Sustainability', 'Cloud Computing', 'Blockchain',
    'Mobile Development', 'Web Development', 'Project Management',
    'Business Development', 'Marketing', 'Sales', 'Finance',
    'Operations', 'Strategy', 'Consulting', 'Research'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.full_name?.trim()) newErrors.full_name = 'Full name is required';
    if (!formData?.title?.trim()) newErrors.title = 'Title is required';
    if (formData?.linkedin_url && !isValidUrl(formData?.linkedin_url)) {
      newErrors.linkedin_url = 'Please enter a valid LinkedIn URL';
    }
    if (formData?.website_url && !isValidUrl(formData?.website_url)) {
      newErrors.website_url = 'Please enter a valid URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    const updateData = {
      ...formData,
      skills: formData?.skills?.split(',')?.map(skill => skill?.trim())?.filter(Boolean)
    };

    const result = await onSave?.('team', updateData);
    if (result?.success) {
      // Update team members display
      setTeamMembers(prev => prev?.map(member => 
        member?.isOwner 
          ? { 
              ...member, 
              name: updateData?.full_name, 
              title: updateData?.title,
              bio: updateData?.bio,
              skills: updateData?.skills,
              linkedin_url: updateData?.linkedin_url
            }
          : member
      ));
    }
  };

  const handleAddMember = () => {
    if (!newMember?.name?.trim() || !newMember?.title?.trim()) return;

    const memberToAdd = {
      id: teamMembers?.length + 1,
      name: newMember?.name,
      title: newMember?.title,
      bio: newMember?.bio,
      skills: newMember?.skills?.split(',')?.map(skill => skill?.trim())?.filter(Boolean),
      linkedin_url: newMember?.linkedin_url,
      isOwner: false
    };

    setTeamMembers(prev => [...prev, memberToAdd]);
    setNewMember({ name: '', title: '', bio: '', skills: '', linkedin_url: '' });
    setShowAddMemberModal(false);
  };

  const handleRemoveMember = (id) => {
    if (teamMembers?.find(member => member?.id === id && member?.isOwner)) return;
    setTeamMembers(prev => prev?.filter(member => member?.id !== id));
  };

  const addSkill = (skill) => {
    const currentSkills = formData?.skills ? formData?.skills?.split(',')?.map(s => s?.trim()) : [];
    if (!currentSkills?.includes(skill)) {
      setFormData(prev => ({
        ...prev,
        skills: prev?.skills ? `${prev?.skills}, ${skill}` : skill
      }));
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Team & Expertise</h3>
        <p className="text-sm text-gray-600 mb-6">
          Showcase your team members, their expertise, and professional backgrounds to build credibility.
        </p>
      </div>

      {/* Owner Profile Form */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h4 className="text-md font-medium text-gray-900 mb-4">Your Profile (Owner)</h4>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Input
                label="Full Name *"
                name="full_name"
                value={formData?.full_name}
                onChange={handleInputChange}
                error={errors?.full_name}
                placeholder="Your full name"
              />
            </div>

            <div>
              <Input
                label="Title *"
                name="title"
                value={formData?.title}
                onChange={handleInputChange}
                error={errors?.title}
                placeholder="CEO, CTO, Founder, etc."
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Professional Bio
            </label>
            <textarea
              name="bio"
              value={formData?.bio}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Tell potential partners about your background, experience, and expertise..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Skills & Expertise
            </label>
            <Input
              name="skills"
              value={formData?.skills}
              onChange={handleInputChange}
              placeholder="Smart Cities, IoT, Data Analytics, Project Management (comma separated)"
            />
            
            {/* Skill Suggestions */}
            <div className="mt-3">
              <p className="text-sm text-gray-600 mb-2">Quick add skills:</p>
              <div className="flex flex-wrap gap-2">
                {skillSuggestions?.slice(0, 8)?.map((skill) => (
                  <button
                    key={skill}
                    type="button"
                    onClick={() => addSkill(skill)}
                    className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                  >
                    + {skill}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Input
                label="LinkedIn Profile"
                name="linkedin_url"
                type="url"
                value={formData?.linkedin_url}
                onChange={handleInputChange}
                error={errors?.linkedin_url}
                placeholder="https://linkedin.com/in/yourprofile"
              />
            </div>

            <div>
              <Input
                label="Personal Website"
                name="website_url"
                type="url"
                value={formData?.website_url}
                onChange={handleInputChange}
                error={errors?.website_url}
                placeholder="https://yourpersonalsite.com"
              />
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t">
            <Button type="submit" disabled={saving}>
              {saving ? 'Updating...' : 'Update Profile'}
            </Button>
          </div>
        </form>
      </div>

      {/* Team Members Section */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h4 className="text-md font-medium text-gray-900">Team Members</h4>
          <Button
            variant="outline"
            onClick={() => setShowAddMemberModal(true)}
          >
            <Icon name="UserPlus" className="w-4 h-4 mr-2" />
            Add Team Member
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {teamMembers?.map((member) => (
            <div key={member?.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h5 className="text-lg font-medium text-gray-900">{member?.name}</h5>
                  <p className="text-sm text-blue-600">{member?.title}</p>
                  {member?.isOwner && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-1">
                      Owner
                    </span>
                  )}
                </div>
                
                {!member?.isOwner && (
                  <button
                    onClick={() => handleRemoveMember(member?.id)}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <Icon name="X" className="w-4 h-4" />
                  </button>
                )}
              </div>

              {member?.bio && (
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">{member?.bio}</p>
              )}

              {member?.skills?.length > 0 && (
                <div className="mb-4">
                  <div className="flex flex-wrap gap-1">
                    {member?.skills?.slice(0, 3)?.map((skill, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700"
                      >
                        {skill}
                      </span>
                    ))}
                    {member?.skills?.length > 3 && (
                      <span className="text-xs text-gray-500">
                        +{member?.skills?.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              {member?.linkedin_url && (
                <a
                  href={member?.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm"
                >
                  <Icon name="Linkedin" className="w-4 h-4 mr-1" />
                  LinkedIn
                </a>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Add Team Member Modal */}
      {showAddMemberModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Add Team Member</h3>
                <button
                  onClick={() => setShowAddMemberModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icon name="X" className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Input
                    label="Full Name *"
                    value={newMember?.name}
                    onChange={(e) => setNewMember(prev => ({ ...prev, name: e?.target?.value }))}
                    placeholder="Team member name"
                  />
                </div>

                <div>
                  <Input
                    label="Title *"
                    value={newMember?.title}
                    onChange={(e) => setNewMember(prev => ({ ...prev, title: e?.target?.value }))}
                    placeholder="CTO, Developer, Designer, etc."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bio
                </label>
                <textarea
                  value={newMember?.bio}
                  onChange={(e) => setNewMember(prev => ({ ...prev, bio: e?.target?.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Brief professional background..."
                />
              </div>

              <div>
                <Input
                  label="Skills"
                  value={newMember?.skills}
                  onChange={(e) => setNewMember(prev => ({ ...prev, skills: e?.target?.value }))}
                  placeholder="Skills (comma separated)"
                />
              </div>

              <div>
                <Input
                  label="LinkedIn Profile"
                  type="url"
                  value={newMember?.linkedin_url}
                  onChange={(e) => setNewMember(prev => ({ ...prev, linkedin_url: e?.target?.value }))}
                  placeholder="https://linkedin.com/in/profile"
                />
              </div>

              <div className="flex justify-end space-x-4 pt-6 border-t">
                <Button
                  variant="outline"
                  onClick={() => setShowAddMemberModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddMember}
                  disabled={!newMember?.name?.trim() || !newMember?.title?.trim()}
                >
                  Add Member
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Certifications Section */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h4 className="text-md font-medium text-gray-900 mb-4">Certifications & Achievements</h4>
        <div className="text-center py-8">
          <Icon name="Award" className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">Add certifications and achievements to showcase your team's credentials.</p>
          <Button variant="outline" size="sm">
            Add Certification
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TeamExpertise;